import * as ChannelService from '@channel.io/channel-web-sdk-loader';
import { useMemo } from 'react';
import ReactGA from 'react-ga4';
import { useLocation } from 'react-router-dom';
import { ampli } from '../../ampli';
import { flarelaneEventKeys } from '../../constants/flarelaneEvent/flarelaneEvent.contant';
import { IProduct } from '../../interfaces/Product.interfaces.d';
import { postMessageToRN } from '../../utils/RNCommunicator/postMessageToRN.util';
import {
  getGeneralMarketDetailPageURL,
  IProductForGetDetailPageURL,
} from '../../utils/getter/getProductDetailPageURL.util';
import { usePostInquiryTractorMutation } from '../api/inquiry/mutations/usePostInquiryTractor.mutation';
import { useCheckUserLoggedIn } from '../user/useCheckUserLoggedIn';
import { usePatchFlarelaneUserTags } from '../api/flarelane/usePatchFlarelaneUserTags';
import { useGetUserProfile } from '../user/useGetUserProfile';

export type IProductForInquiry = Pick<
  IProduct,
  'id' | 'category' | 'title' | 'code' | 'machine' | 'manufacturedYear' | 'operationTime'
>;

interface IUseHandleProductInquiryProps {
  product: IProductForInquiry;
  pageType: 'home' | 'pdp' | 'plp';
}

interface IUseHandleProductInquiryReturn {
  handleProductInquiryButtonClick: () => void;
}

export const useHandleProductInquiry = ({
  product,
  pageType,
}: IUseHandleProductInquiryProps): IUseHandleProductInquiryReturn => {
  const location = useLocation();
  const { isUserLoggedIn } = useCheckUserLoggedIn();
  const { userProfile } = useGetUserProfile();
  const { mutate: postUserInquiryTractor } = usePostInquiryTractorMutation();
  const { mutate: patchFlarelaneUserTags } = usePatchFlarelaneUserTags();

  const productDetailURL = useMemo(() => {
    if (!product) {
      return '';
    }

    return `${window.location.origin}${getGeneralMarketDetailPageURL(product as IProductForGetDetailPageURL)}`;
  }, [product]);

  const handleButtonClick = () => {
    if (product) {
      if (product.category === 'PLUVA') {
        ampli.마켓플루바매물전화상담버튼클릭({
          '게시글 제목': product?.title ?? '',
          '매물 코드': product?.code,
          page_type: pageType,
        });
      } else {
        ampli.마켓파트너매물전화상담버튼클릭({
          '게시글 제목': product?.title ?? '',
          '매물 코드': product?.code,
          page_type: pageType,
        });
      }
    }

    if (userProfile) {
      patchFlarelaneUserTags({ lastInquiredVerifiedProduct: new Date().toISOString() });
    }

    ChannelService.track('ConsultButtonClick');
    ampli.pvmProductInquiry({
      title: product?.title ?? '',
      type: 'Inbound',
      ps: product?.machine.engineOutputInPs ?? '',
      manufacturedYear: product.manufacturedYear ?? '',
      operationTime: product?.operationTime ?? '',
      page_type: pageType,
    });
    ReactGA.event('pvm_product_inquiry', { category: 'pvm', action: 'pvm_product_inquiry', value: 'Inbound' });

    if (window.ReactNativeWebView && pageType === 'pdp') {
      postMessageToRN({
        messageKey: 'flarelane_trackEvent',
        messageParam: flarelaneEventKeys.pvm_pdp_inquiry_button_click,
        messageData: {
          brandName: product.machine.brand.name,
          machineModel: product.machine.model,
          productDetailPageURL: productDetailURL,
        },
      });
    }

    if (isUserLoggedIn) {
      postUserInquiryTractor({ reqBody: { productId: product.id } });

      if (window.ReactNativeWebView) {
        postMessageToRN({ messageKey: 'link', messageParam: 'tel:0316074997' });
      } else {
        window.location.href = 'tel:0316074997';
      }
    } else {
      sessionStorage.setItem('blockedPageURL', location.pathname);
      sessionStorage.setItem('failedInquiryTractorId', product.id);
    }
  };

  return {
    handleProductInquiryButtonClick: handleButtonClick,
  };
};
